import { Fragment, useEffect, useState, type ReactElement } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Petal, Sparkle, ELEMENT_INFO, ANIMAL_INFO } from '../design-system'
import type { Animal, Element } from '@origami/shared'
import { useTitleHandlers } from './useTitleHandlers'
import { RotatePrompt, useIsMobile, useIsFoldOpen } from './useIsMobile'
import { TitleToast } from './TitleToast'
import { MultiTwinScrolls } from './MultiTwinScrolls'
import './TitleScreenA.css'

const ANIMALS: Animal[]  = ['bird', 'mammal', 'reptile', 'fish', 'insect']
const ELEMENTS: Element[] = ['fire', 'water', 'wind', 'wood', 'earth']

const MENU = [
  {
    id: 'solo' as const,
    glyph: '折',
    label: 'Begin Solo Run',
    sub: 'play a quick draft',
    accent: 'var(--el-fire-deep)',
  },
  {
    id: 'lobby' as const,
    glyph: '連',
    label: 'Make a Lobby',
    sub: 'fold with friends',
    accent: 'var(--el-wood-deep)',
  },
  {
    id: 'join' as const,
    glyph: '鍵',
    label: 'Join with Code',
    sub: 'enter a 6-letter scroll',
    accent: 'var(--el-water)',
  },
  {
    id: 'codex' as const,
    glyph: '巻',
    label: 'Compendium',
    sub: 'leaf the bestiary',
    accent: 'var(--gold-deep)',
  },
]

// Rotating featured creature — single stage below the hero crane.
// Species cycle Phoenix → Anvil Boar → Tinder Fox; each cycles its
// own ★1→★2→★3 with sparkle bursts. 18s loop = 6s per species.
const FEATURED = [
  { id: 'phoenix', label: 'Phoenix'    },
  { id: 'boar',    label: 'Anvil Boar' },
  { id: 'fox',     label: 'Tinder Fox' },
]

interface TitleScreenAProps {
  /** When mounted from the `?multi` route this is `'multi'`, which
   *  starts the page in multi-entry mode (twin scrolls visible from
   *  the first frame). Direct title visits use `'menu'` (default).
   *  The page can flip back to menu via the hanko / ESC; on exit
   *  it navigates back to `?title=a`. */
  initialMode?: 'menu' | 'multi'
}

export function TitleScreenA({ initialMode = 'menu' }: TitleScreenAProps = {}): ReactElement {
  const h = useTitleHandlers()
  const isMobile = useIsMobile()
  const foldOpen = useIsFoldOpen()
  const [isFs, setIsFs] = useState(
    typeof document !== 'undefined' && !!document.fullscreenElement,
  )
  // `multiMode` is the visible state of the twin-scrolls overlay.
  // Initialized from the prop (which mirrors the URL via App.tsx
  // routing). Setting it to false triggers the overlay's exit
  // animation; onExitComplete navigates back to the menu URL so the
  // animation finishes before the route change.
  const [multiMode, setMultiMode] = useState(initialMode === 'multi')
  const closeMulti = () => setMultiMode(false)

  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  // Persist current variant so / resolves here on the next visit.
  useEffect(() => {
    try { window.localStorage.setItem('origami-theme', 'a') } catch { /* ignore */ }
  }, [])

  // ESC closes the multi-mode overlay. The overlay's own AnimatePresence
  // exit handler navigates back to the title URL after the animation.
  useEffect(() => {
    if (!multiMode) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMulti()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [multiMode])

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen()
    } else {
      void document.documentElement.requestFullscreen().catch(() => {})
    }
  }

  function switchTheme(target: 'a' | 'b') {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      try { window.localStorage.setItem('origami-theme', target) } catch { /* ignore */ }
      window.history.pushState(null, '', `?title=${target}`)
      window.dispatchEvent(new Event('url-changed'))
    }
  }

  function goToSettings(e: React.MouseEvent) {
    e.preventDefault()
    window.history.pushState(null, '', '?settings')
    window.dispatchEvent(new Event('url-changed'))
  }

  function handleClick(id: typeof MENU[number]['id']) {
    switch (id) {
      case 'solo':  return h.hasActiveRun === true ? h.continueRun() : h.startSolo()
      case 'codex': return h.openCompendium()
      case 'lobby': return h.openMultiplayer()
      case 'join':
        if (!h.joinExpanded) return h.expandJoin()
        return h.submitJoin()
    }
  }

  // When the player has an active run, the solo CTA shifts from
  // "Begin Solo Run" to "Continue Run" and an "or start new run"
  // link appears beneath it.
  function resolveItem(item: typeof MENU[number]) {
    if (item.id === 'solo' && h.hasActiveRun === true) {
      return { ...item, label: 'Continue Run', sub: 'resume your folded five' }
    }
    return item
  }

  // When the multi overlay exits (after the close animation
  // completes) navigate back to the title URL. Done via pushState +
  // a synthetic url-changed event so App.tsx re-routes without a
  // hard reload — the menu mounts in the new TitleScreenA instance.
  const navigateBackToMenu = () => {
    window.history.pushState(null, '', '?')
    window.dispatchEvent(new Event('url-changed'))
  }

  return (
    <div className={`tsA${multiMode ? ' tsA--multi' : ''}`}>
      <TitleToast message={h.toast} tone="paper" />

      {h.confirmingNewRun && (
        <div
          className="tsA__confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tsA-confirm-title"
          onClick={h.cancelNewRun}
        >
          <div className="tsA__confirm-card" onClick={(e) => e.stopPropagation()}>
            <h2 id="tsA-confirm-title" className="tsA__confirm-title">
              Start a new run?
            </h2>
            <p className="tsA__confirm-body">
              Your current run will be lost. You'll pick a new mascot,
              name, and team.
            </p>
            <div className="tsA__confirm-actions">
              <button
                type="button"
                className="tsA__confirm-btn tsA__confirm-btn--ghost"
                onClick={h.cancelNewRun}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tsA__confirm-btn tsA__confirm-btn--primary"
                onClick={h.confirmNewRun}
              >
                Start new run
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="tsA__wall" aria-hidden="true" />

      {isMobile ? (
        <MobileTitleA
          h={h}
          isFs={isFs}
          toggleFullscreen={toggleFullscreen}
          switchTheme={switchTheme}
          goToSettings={goToSettings}
          handleClick={handleClick}
        />
      ) : (
      <>
      <img
        className="tsA__cloud tsA__cloud--tl"
        src="/glyph-assets/kumomon.png"
        alt=""
        aria-hidden="true"
      />
      <img
        className="tsA__cloud tsA__cloud--br"
        src="/glyph-assets/kumomon.png"
        alt=""
        aria-hidden="true"
      />

      <main className="tsA__stage">
        {/* === LEFT: scroll holding the action buttons ============ */}
        <section className="tsA__scroll-wrap" aria-label="Menu scroll">
          <img
            className="tsA__scroll"
            src="/title/kakejiku-scroll.webp"
            alt=""
            aria-hidden="true"
          />
          <nav className="tsA__scroll-menu" aria-label="Main menu">
            {MENU.map((raw, i) => {
              const item = resolveItem(raw)
              const expanded = item.id === 'join' && h.joinExpanded
              const showNewLink = item.id === 'solo' && h.hasActiveRun === true
              return (
                <Fragment key={item.id}>
                  <button
                    type="button"
                    className={`tsA__stamp-btn tsA__stamp-btn--${i % 2 === 0 ? 'r' : 'l'}${expanded ? ' tsA__stamp-btn--expanded' : ''}`}
                    onClick={() => handleClick(item.id)}
                    style={{ '--accent': item.accent } as React.CSSProperties}
                  >
                    {expanded ? (
                      <span className="tsA__join">
                        <input
                          ref={h.joinInputRef}
                          className="tsA__join-input"
                          type="text"
                          maxLength={6}
                          value={h.joinCode}
                          onChange={(e) =>
                            h.setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') h.submitJoin()
                            if (e.key === 'Escape') h.collapseJoin()
                          }}
                          placeholder="ABCD12"
                          aria-label="6-letter lobby code"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span
                          className="tsA__join-go"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation()
                            h.submitJoin()
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              h.submitJoin()
                            }
                          }}
                        >
                          Go →
                        </span>
                      </span>
                    ) : (
                      <>
                        <span className="tsA__stamp-glyph" aria-hidden="true">{item.glyph}</span>
                        <span className="tsA__stamp-body">
                          <span className="tsA__stamp-label">{item.label}</span>
                          <span className="tsA__stamp-sub">{item.sub}</span>
                        </span>
                      </>
                    )}
                  </button>
                  {showNewLink && (
                    <button
                      type="button"
                      className="tsA__solo-newlink"
                      onClick={h.requestNewRun}
                    >
                      or start a new run
                    </button>
                  )}
                </Fragment>
              )
            })}
          </nav>
        </section>

        {/* === RIGHT: title block + hero (cast moved to sidebar) === */}
        <section className="tsA__hero-side" aria-label="Game showcase">
          <header className="tsA__title-block">
            <div className="tsA__hanko" aria-hidden="true">
              <span className="tsA__hanko-kanji">折</span>
              <span className="tsA__hanko-kanji">鶴</span>
              <span className="tsA__hanko-wear tsA__hanko-wear--tl" />
              <span className="tsA__hanko-wear tsA__hanko-wear--br" />
            </div>
            <div className="tsA__title-text">
              <h1 className="tsA__roman">Origami Arena</h1>
              <p className="tsA__tagline">
                <em>Fold and evolve an army of tiny paper friends!</em>
              </p>
            </div>
          </header>

          {/* Featured rotating creature — promoted to the central
              hero slot. Single stage cycles all three species through
              their ★1 → ★2 → ★3 morphs on an 18s loop, with a soft
              gold halo and bob animation. */}
          <div className="tsA__featured" aria-label="Evolution showcase">
            <div className="tsA__featured-art bob" aria-hidden="true">
              <div className="tsA__featured-halo" />
              {FEATURED.flatMap((c) =>
                [1, 2, 3].map((star) => (
                  <img
                    key={`${c.id}-${star}`}
                    className={`tsA__feat-tier tsA__feat-tier--${c.id}-${star}`}
                    src={`/glyph-assets/${c.id}-${star}.png`}
                    alt=""
                    aria-hidden="true"
                  />
                )),
              )}
              <span className="tsA__feat-burst" aria-hidden="true" />
            </div>
            <div className="tsA__featured-meta">
              <div className="tsA__feat-stars" aria-hidden="true">
                <span className="tsA__feat-star tsA__feat-star--1">★</span>
                <span className="tsA__feat-star tsA__feat-star--2">★★</span>
                <span className="tsA__feat-star tsA__feat-star--3">★★★</span>
              </div>
              <div className="tsA__feat-names" aria-hidden="true">
                {FEATURED.map((c) => (
                  <span key={c.id} className={`tsA__feat-name tsA__feat-name--${c.id}`}>
                    {c.label}
                  </span>
                ))}
              </div>
              <div className="tsA__feat-tagline">
                <span>fold three to evolve</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Petal
        size={20}
        color="#d9b769"
        style={{
          position: 'absolute',
          top: '28%',
          left: '42%',
          animation: 'drift 9s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <Petal
        size={16}
        color="#e8c574"
        style={{
          position: 'absolute',
          top: '62%',
          left: '40%',
          animation: 'drift 12s ease-in-out -4s infinite',
          pointerEvents: 'none',
        }}
      />

      <footer className="tsA__footer">
        <span className="tsA__footer-left">
          <span className="tsA__version">v 0.1</span>
          <button
            type="button"
            className="tsA__fs-btn"
            onClick={toggleFullscreen}
            aria-pressed={isFs}
          >
            <span className="tsA__fs-glyph" aria-hidden="true">
              {isFs ? '⤡' : '⤢'}
            </span>
            <span>
              {foldOpen
                ? isFs ? 'Exit' : 'Fullscreen'
                : isFs ? 'Exit Full Screen' : 'View Full Screen'}
            </span>
          </button>
        </span>
        <span className="tsA__cue">
          <span className="tsA__cue-group">
            <span>{foldOpen ? '5 animals' : 'five animals'}</span>
            <span className="tsA__cue-chips" aria-hidden="true">
              {ANIMALS.map((a) => (
                <span
                  key={a}
                  className="tsA__cue-chip"
                  style={{ background: ANIMAL_INFO[a].bg }}
                  title={ANIMAL_INFO[a].en}
                >
                  {ANIMAL_INFO[a].kanji}
                </span>
              ))}
            </span>
          </span>
          <span className="tsA__cue-sep" aria-hidden="true">·</span>
          <span className="tsA__cue-group">
            <span>{foldOpen ? '5 elements' : 'five elements'}</span>
            <span className="tsA__cue-chips" aria-hidden="true">
              {ELEMENTS.map((e) => (
                <span
                  key={e}
                  className="tsA__cue-chip"
                  style={{
                    background: ELEMENT_INFO[e].bg,
                    color: ELEMENT_INFO[e].fg,
                  }}
                  title={ELEMENT_INFO[e].en}
                >
                  {ELEMENT_INFO[e].kanji}
                </span>
              ))}
            </span>
          </span>
          <span className="tsA__cue-sep" aria-hidden="true">·</span>
          <span>{foldOpen ? '25 mins' : 'twenty-five minutes to play'}</span>
        </span>
        <span className="tsA__footer-right">
          <span className="tsA__health">
            <span
              className={`tsA__dot tsA__dot--${h.health.phase}`}
              aria-hidden="true"
            />
            <span>
              {h.health.phase === 'connecting'
                ? 'connecting…'
                : h.health.phase === 'online'
                  ? `online · ${h.health.units} units`
                  : 'offline'}
            </span>
          </span>
          <span className="tsA__theme" role="group" aria-label="Theme">
            <a
              href="?title=a"
              onClick={switchTheme('a')}
              className="tsA__theme-opt tsA__theme-opt--active"
              title="Light theme"
              aria-current="page"
            >
              <span aria-hidden="true">☀</span>
            </a>
            <a
              href="?title=b"
              onClick={switchTheme('b')}
              className="tsA__theme-opt"
              title="Dark theme"
            >
              <span aria-hidden="true">☾</span>
            </a>
          </span>
          <a
            href="?settings"
            onClick={goToSettings}
            className="tsA__cog"
            title="Settings"
            aria-label="Settings"
          >
            <img
              className="tsA__cog-icon"
              src="/ui-assets/settings-gear.webp"
              alt=""
              aria-hidden="true"
            />
          </a>
        </span>
      </footer>
      </>
      )}

      {/* Multi-entry overlay. AnimatePresence lets the scrolls
          animate IN on mount and OUT before we navigate back to the
          menu URL. Setting `multiMode` to false removes the child;
          `onExitComplete` fires after the close animation finishes
          and triggers the route change. */}
      <AnimatePresence onExitComplete={navigateBackToMenu}>
        {multiMode && (
          <div className="tsA__multi-overlay" key="multi-overlay">
            <button
              type="button"
              className="tsA__multi-close"
              onClick={closeMulti}
              aria-label="Close multiplayer"
              title="Back to title"
            >
              <span aria-hidden>‹</span>
              <span className="tsA__multi-close-label">Back</span>
            </button>
            <MultiTwinScrolls compact={isMobile} />
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ============================================================
   Mobile fork — LANDSCAPE-locked. Portrait phones get the
   <RotatePrompt /> overlay (CSS gates visibility); landscape
   phones see a 2-column layout with title + featured stage on
   the left and the stamp-button menu stacked on the right. */
interface MobileProps {
  h: ReturnType<typeof useTitleHandlers>
  isFs: boolean
  toggleFullscreen: () => void
  switchTheme: (target: 'a' | 'b') => (e: React.MouseEvent) => void
  goToSettings: (e: React.MouseEvent) => void
  handleClick: (id: typeof MENU[number]['id']) => void
}

function MobileTitleA({ h, isFs, toggleFullscreen, switchTheme, goToSettings, handleClick }: MobileProps): ReactElement {
  function resolveItem(item: typeof MENU[number]) {
    if (item.id === 'solo' && h.hasActiveRun === true) {
      return { ...item, label: 'Continue Run', sub: 'resume your folded five' }
    }
    return item
  }
  return (
    <>
      <RotatePrompt />

      <main className="tsA-m__stage">
        <section className="tsA-m__left">
          <header className="tsA-m__title">
            <div className="tsA__hanko" aria-hidden="true">
              <span className="tsA__hanko-kanji">折</span>
              <span className="tsA__hanko-kanji">鶴</span>
              <span className="tsA__hanko-wear tsA__hanko-wear--tl" />
              <span className="tsA__hanko-wear tsA__hanko-wear--br" />
            </div>
            <div className="tsA-m__title-text">
              <h1 className="tsA-m__roman">Origami Arena</h1>
              <p className="tsA-m__tagline">
                <em>Fold and evolve an army of tiny paper friends!</em>
              </p>
            </div>
          </header>

          <div className="tsA-m__featured">
            <div className="tsA__featured-art bob" aria-hidden="true">
              <div className="tsA__featured-halo" />
              {FEATURED.flatMap((c) =>
                [1, 2, 3].map((star) => (
                  <img
                    key={`${c.id}-${star}`}
                    className={`tsA__feat-tier tsA__feat-tier--${c.id}-${star}`}
                    src={`/glyph-assets/${c.id}-${star}.png`}
                    alt=""
                    aria-hidden="true"
                  />
                )),
              )}
              <span className="tsA__feat-burst" aria-hidden="true" />
            </div>
            <div className="tsA__featured-meta">
              <div className="tsA__feat-stars" aria-hidden="true">
                <span className="tsA__feat-star tsA__feat-star--1">★</span>
                <span className="tsA__feat-star tsA__feat-star--2">★★</span>
                <span className="tsA__feat-star tsA__feat-star--3">★★★</span>
              </div>
              <div className="tsA__feat-names" aria-hidden="true">
                {FEATURED.map((c) => (
                  <span key={c.id} className={`tsA__feat-name tsA__feat-name--${c.id}`}>
                    {c.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <nav className="tsA-m__menu" aria-label="Main menu">
          {MENU.map((raw) => {
            const item = resolveItem(raw)
            const expanded = item.id === 'join' && h.joinExpanded
            const showNewLink = item.id === 'solo' && h.hasActiveRun === true
            return (
              <Fragment key={item.id}>
                <button
                  type="button"
                  className={`tsA-m__btn${expanded ? ' tsA-m__btn--expanded' : ''}`}
                  onClick={() => handleClick(item.id)}
                  style={{ '--accent': item.accent } as React.CSSProperties}
                >
                  {expanded ? (
                    <span className="tsA__join">
                      <input
                        ref={h.joinInputRef}
                        className="tsA__join-input"
                        type="text"
                        maxLength={6}
                        value={h.joinCode}
                        onChange={(e) =>
                          h.setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') h.submitJoin()
                          if (e.key === 'Escape') h.collapseJoin()
                        }}
                        placeholder="ABCD12"
                        aria-label="6-letter lobby code"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span
                        className="tsA__join-go"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation()
                          h.submitJoin()
                        }}
                      >
                        Go →
                      </span>
                    </span>
                  ) : (
                    <>
                      <span className="tsA__stamp-glyph" aria-hidden="true">{item.glyph}</span>
                      <span className="tsA__stamp-body">
                        <span className="tsA__stamp-label">{item.label}</span>
                        <span className="tsA__stamp-sub">{item.sub}</span>
                      </span>
                    </>
                  )}
                </button>
                {showNewLink && (
                  <button
                    type="button"
                    className="tsA__solo-newlink"
                    onClick={h.requestNewRun}
                  >
                    or start a new run
                  </button>
                )}
              </Fragment>
            )
          })}
        </nav>
      </main>

      <footer className="tsA-m__footer">
        <span className="tsA-m__footer-left">
          <button
            type="button"
            className="tsA__fs-btn"
            onClick={toggleFullscreen}
            aria-pressed={isFs}
          >
            <span className="tsA__fs-glyph" aria-hidden="true">
              {isFs ? '⤡' : '⤢'}
            </span>
            <span>{isFs ? 'Exit' : 'Fullscreen'}</span>
          </button>
          <span className="tsA__version">v 0.1</span>
        </span>
        <span className="tsA-m__footer-right">
          <span className="tsA__health">
            <span
              className={`tsA__dot tsA__dot--${h.health.phase}`}
              aria-hidden="true"
            />
            <span>
              {h.health.phase === 'connecting'
                ? 'connecting…'
                : h.health.phase === 'online'
                  ? `online · ${h.health.units} units`
                  : 'offline'}
            </span>
          </span>
          <span className="tsA__theme" role="group" aria-label="Theme">
            <a
              href="?title=a"
              onClick={switchTheme('a')}
              className="tsA__theme-opt tsA__theme-opt--active"
              title="Light theme"
              aria-current="page"
            >
              <span aria-hidden="true">☀</span>
            </a>
            <a
              href="?title=b"
              onClick={switchTheme('b')}
              className="tsA__theme-opt"
              title="Dark theme"
            >
              <span aria-hidden="true">☾</span>
            </a>
          </span>
          <a
            href="?settings"
            onClick={goToSettings}
            className="tsA__cog"
            title="Settings"
            aria-label="Settings"
          >
            <img
              className="tsA__cog-icon"
              src="/ui-assets/settings-gear.webp"
              alt=""
              aria-hidden="true"
            />
          </a>
        </span>
      </footer>
    </>
  )
}
